// ============================================================================
// EMISSION REPORTS
// ============================================================================
// Report generation and download for emissions data
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { emissionsApi } from '../api/emissions.api';
import PageInfoButton from '@/components/PageInfoButton';

interface Report {
  id: number;
  name: string;
  report_type: string;
  period_start: string;
  period_end: string;
  gwp_standard?: string;
  total_co2e?: number;
  status?: string;
  created_at?: string;
}

interface GenerateForm {
  name: string;
  report_type: string;
  period_start: string;
  period_end: string;
  gwp_standard: string;
}

const DEFAULT_FORM: GenerateForm = {
  name: '',
  report_type: 'annual',
  period_start: '',
  period_end: '',
  gwp_standard: 'AR5',
};

const statusColor = (status?: string) => {
  switch (status) {
    case 'completed': return 'bg-green-100 text-green-700';
    case 'processing': return 'bg-blue-100 text-blue-700';
    case 'failed': return 'bg-red-100 text-red-700';
    default: return 'bg-slate-100 text-slate-600';
  }
};

const EmissionReports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [form, setForm] = useState<GenerateForm>(DEFAULT_FORM);

  const loadReports = useCallback(async () => {
    try {
      const data = await emissionsApi.getReports();
      setReports(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load reports');
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadReports();
      setLoading(false);
    };
    init();
  }, [loadReports]);

  const generateReport = async () => {
    if (!form.name.trim() || !form.period_start || !form.period_end) return;
    try {
      setGenerating(true);
      const payload: Parameters<typeof emissionsApi.generateReport>[0] = {
        name: form.name,
        report_type: form.report_type,
        period_start: form.period_start,
        period_end: form.period_end,
      };
      if (form.gwp_standard) payload.gwp_standard = form.gwp_standard;
      await emissionsApi.generateReport(payload);
      setShowGenerate(false);
      setForm(DEFAULT_FORM);
      await loadReports();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = async (id: number, name: string) => {
    try {
      const blob = await emissionsApi.downloadReport(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name.replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to download report');
    }
  };

  const deleteReport = async (id: number) => {
    try {
      await emissionsApi.deleteReport(id);
      await loadReports();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete report');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Emission Reports</h1><PageInfoButton /></div>
          <p className="text-slate-500 mt-1">Generate and download structured emissions reports</p>
        </div>
        <button
          onClick={() => setShowGenerate(true)}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
        >
          + Generate Report
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Reports Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-sm font-semibold text-slate-700">Generated Reports</h2>
        </div>
        {reports.length === 0 ? (
          <div className="px-6 py-8 text-center text-slate-400 text-sm">
            No reports generated yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Period</th>
                  <th className="px-4 py-3 text-right">Total CO₂e</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reports.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-left font-medium text-slate-800">{r.name}</td>
                    <td className="px-4 py-3 text-left text-slate-600 capitalize">{r.report_type}</td>
                    <td className="px-4 py-3 text-left text-slate-600">
                      {r.period_start ? new Date(r.period_start).toLocaleDateString() : '—'}
                      {' → '}
                      {r.period_end ? new Date(r.period_end).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-left font-semibold text-slate-800">
                      {r.total_co2e !== undefined
                        ? r.total_co2e.toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusColor(r.status)}`}>
                        {r.status ?? 'pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left text-slate-500 text-xs">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => downloadReport(r.id, r.name)}
                          className="px-2 py-1 text-xs bg-emerald-50 border border-emerald-200 rounded text-emerald-700 hover:bg-emerald-100 transition-colors"
                        >
                          Download CSV
                        </button>
                        <button
                          onClick={() => deleteReport(r.id)}
                          className="px-2 py-1 text-xs border border-red-200 rounded text-red-600 hover:bg-red-50 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generate Report Modal */}
      {showGenerate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg mx-4">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Generate Report</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Report Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. Annual Emissions Report 2024"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Report Type</label>
                  <select
                    value={form.report_type}
                    onChange={e => setForm(f => ({ ...f, report_type: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="annual">Annual</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">GWP Standard</label>
                  <select
                    value={form.gwp_standard}
                    onChange={e => setForm(f => ({ ...f, gwp_standard: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="AR4">AR4</option>
                    <option value="AR5">AR5</option>
                    <option value="AR6">AR6</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Period Start *</label>
                  <input
                    type="date"
                    value={form.period_start}
                    onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Period End *</label>
                  <input
                    type="date"
                    value={form.period_end}
                    onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowGenerate(false); setForm(DEFAULT_FORM); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={generateReport}
                disabled={generating || !form.name.trim() || !form.period_start || !form.period_end}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {generating ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmissionReports;
